import { useState, useCallback } from 'react';
import * as userService from '../services/userService';
import { IUser } from '../types/interfaces/IUser';
import { useUIStore } from '../stores/uiStore';
import { getApiErrorMessage } from '../services/core/apiResponseHelpers';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';

type ProfileEditablePayload = Pick<
  IUser,
  'name' | 'avatar' | 'phone' | 'address'
>;

type ProfileUpdatePayload = Partial<ProfileEditablePayload> | { base64?: string; url?: string };

export const useProfile = () => {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useUIStore();

  const getProfile = useCallback(
    async (userId: string) => {
      setLoading(true);
      try {
        const response = await userService.getUserById(userId);
        if (response.success && response.data) {
          return response;
        } else {
          addNotification({
            type: 'error',
            title: 'Error',
            message: getApiErrorMessage(response, 'Error al cargar el perfil'),
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al cargar el perfil',
        });
        return {
          success: false,
          data: null,
          error: 'Error al cargar el perfil',
        };
      } finally {
        setLoading(false);
      }
    },
    [] // Remover addNotification de dependencias para evitar loops
  );

  const updateProfile = useCallback(
    async (userId: string, userData: ProfileUpdatePayload): Promise<{ success: boolean; data?: IUser | null; [key: string]: any }> => {
      setLoading(true);
      try {
        // Si contiene base64 o url, mapear a avatar/avatarUrl
        const processedData: any = { ...userData };
        if ('base64' in userData && userData.base64) {
          processedData.avatar = userData.base64;
          delete processedData.base64;
        }
        if ('url' in userData && userData.url) {
          processedData.avatarUrl = userData.url;
          delete processedData.url;
        }

        const response = await userService.updateUser(userId, processedData);
        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Perfil Actualizado', 'Los cambios se han guardado correctamente');
          return response;
        } else {
          addNotification({
            type: 'error',
            title: 'Error',
            message: getApiErrorMessage(response, 'Error al actualizar el perfil'),
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al actualizar el perfil',
        });
        return {
          success: false,
          data: null,
          error: 'Error al actualizar el perfil',
        } as any;
      } finally {
        setLoading(false);
      }
    },
    [] // Remover addNotification de dependencias para evitar loops
  );

  return {
    getProfile,
    updateProfile,
    loading,
  };
};
